import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RemoveCircle as RemoveIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { env } from "../../utilities/function";
import { getCookie } from "../../utilities/cookies";
import RowPerPage from "../../components/RowPerPage";
import Pagination from "../../components/Pagination";
import { useLocation } from "react-router-dom";

const API_BASE_URL = env("SERVER") + "/api";

const fetchPlans = async (page = 1, limit = 100, search = "", type = "", interval = "", isEnabled = "") => {
  const accessToken = getCookie("accessToken");
  const response = await axios.get(`${API_BASE_URL}/plans?page=${page}&limit=${limit}&search=${search}&type=${type}&interval=${interval}&isEnabled=${isEnabled}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.data;
};

const togglePlanStatus = async ({ id, isEnabled }) => {
  const accessToken = getCookie("accessToken");
  return await axios.patch(
    `${API_BASE_URL}/plans/${id}/toggle`,
    { isEnabled },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
};

const createPlan = async (data) => {
  const accessToken = getCookie("accessToken");
  return await axios.post(`${API_BASE_URL}/plans`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const updatePlan = async ({ id, data }) => {
  const accessToken = getCookie("accessToken");
  return await axios.put(`${API_BASE_URL}/plans/${id}`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const deletePlan = async (id) => {
  const accessToken = getCookie("accessToken");
  return await axios.delete(`${API_BASE_URL}/plans/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const PlanForm = ({ open, onClose, plan, onSubmit, isEditing }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    interval: "month",
    amount: 0,
    title: "",
    type: "hr",
    permission: [],
  });

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name || "",
        description: plan.description || "",
        interval: plan.interval || "month",
        amount: plan.amount || 0,
        title: plan.title || "",
        type: plan.type || "hr",
        permission: Array.isArray(plan.permission) ? plan.permission : [],
      });
    } else {
      setFormData({
        name: "",
        description: "",
        interval: "month",
        amount: 0,
        title: "",
        type: "hr",
        permission: [],
      });
    }
  }, [plan, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleAddPermission = () => {
    setFormData((prev) => ({
      ...prev,
      permission: [
        ...prev.permission,
        { isPermission: true, isShow: true, content: "", isUnlimited: false, noOfAllowed: 0, key: "", format: "", storage_unit: "" },
      ],
    }));
  };

  const handleRemovePermission = (index) => {
    setFormData((prev) => ({
      ...prev,
      permission: prev.permission.filter((_, i) => i !== index),
    }));
  };

  const handlePermissionChange = (index, field, value) => {
    setFormData((prev) => {
      const newPermissions = [...prev.permission];
      newPermissions[index] = { ...newPermissions[index], [field]: value };
      return { ...prev, permission: newPermissions };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEditing ? "Edit Plan" : "Create New Plan"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name" value={formData.name} onChange={handleChange("name")} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Title" value={formData.title} onChange={handleChange("title")} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={formData.description} onChange={handleChange("description")} multiline rows={2} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Interval</InputLabel>
                <Select value={formData.interval} onChange={handleChange("interval")} label="Interval">
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="year">Year</MenuItem>
                  <MenuItem value="one-time">One-time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Amount" type="number" value={formData.amount} onChange={handleChange("amount")} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>App Type</InputLabel>
                <Select value={formData.type} onChange={handleChange("type")} label="App Type">
                  <MenuItem value="hr">HR</MenuItem>
                  <MenuItem value="files">Files</MenuItem>
                  <MenuItem value="esign">Esign</MenuItem>
                  <MenuItem value="projects">Projects</MenuItem>
                  <MenuItem value="chat">Chat</MenuItem>
                  <MenuItem value="app">App</MenuItem>
                  <MenuItem value="campaigns">Campaigns</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Permissions</Typography>
                <Button startIcon={<AddIcon />} onClick={handleAddPermission} variant="outlined" size="small">
                  Add Permission
                </Button>
              </Box>
              {formData.permission.map((perm, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, borderRadius: 1 }} className="border border-gray-300 dark:border-gray-600">
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Key"
                        value={perm.key}
                        onChange={(e) => handlePermissionChange(index, "key", e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Content"
                        value={perm.content}
                        onChange={(e) => handlePermissionChange(index, "content", e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Allowed Count"
                        type="number"
                        value={perm.noOfAllowed}
                        onChange={(e) => handlePermissionChange(index, "noOfAllowed", parseInt(e.target.value))}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Format/Unit"
                        value={perm.format || perm.storage_unit || ""}
                        onChange={(e) => handlePermissionChange(index, "format", e.target.value)}
                        size="small"
                        placeholder="e.g. GB"
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton color="error" onClick={() => handleRemovePermission(index)}>
                        <RemoveIcon />
                      </IconButton>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unlimited</InputLabel>
                        <Select
                          value={String(perm.isUnlimited)}
                          onChange={(e) => handlePermissionChange(index, "isUnlimited", e.target.value === "true")}
                          label="Unlimited"
                        >
                          <MenuItem value="false">No</MenuItem>
                          <MenuItem value="true">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Is Permission</InputLabel>
                        <Select
                          value={String(perm.isPermission ?? true)}
                          onChange={(e) => handlePermissionChange(index, "isPermission", e.target.value === "true")}
                          label="Is Permission"
                        >
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Is Show</InputLabel>
                        <Select
                          value={String(perm.isShow ?? true)}
                          onChange={(e) => handlePermissionChange(index, "isShow", e.target.value === "true")}
                          label="Is Show"
                        >
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">{isEditing ? "Update" : "Create"}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Plans = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [intervalFilter, setIntervalFilter] = useState("");
  const [isEnabledFilter, setIsEnabledFilter] = useState("");

  // Handle direct "New" action from Navbar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "new") {
      setEditingPlan(null);
      setDialogOpen(true);
    }
  }, [location.search]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: plansData, isLoading, error } = useQuery({
    queryKey: ["plans", page, rowsPerPage, debouncedSearch, typeFilter, intervalFilter, isEnabledFilter],
    queryFn: () => fetchPlans(page, rowsPerPage, debouncedSearch, typeFilter, intervalFilter, isEnabledFilter),
  });

  const createMutation = useMutation({
    mutationFn: createPlan,
    onSuccess: () => {
      queryClient.invalidateQueries(["plans"]);
      setDialogOpen(false);
      setSnackbar({ open: true, message: "Plan created successfully!", severity: "success" });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Error creating plan: ${error.message}`,
        severity: "error",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updatePlan,
    onSuccess: () => {
      queryClient.invalidateQueries(["plans"]);
      setDialogOpen(false);
      setEditingPlan(null);
      setSnackbar({ open: true, message: "Plan updated successfully!", severity: "success" });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Error updating plan: ${error.message}`,
        severity: "error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePlan,
    onSuccess: () => {
      queryClient.invalidateQueries(["plans"]);
      setDeleteConfirmOpen(false);
      setPlanToDelete(null);
      setSnackbar({ open: true, message: "Plan deleted successfully!", severity: "success" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: togglePlanStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(["plans"]);
      setSnackbar({
        open: true,
        message: "Plan status updated!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Error updating status: ${error.message}`,
        severity: "error",
      });
    },
  });

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    setPlanToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (planToDelete) {
      deleteMutation.mutate(planToDelete);
    }
  };

  const handleToggle = (plan) => {
    toggleMutation.mutate({ id: plan._id, isEnabled: !plan.isEnabled });
  };

  const handleSubmit = (data) => {
    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan._id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  if (error) return <Alert severity="error">Error loading plans</Alert>;

  return (
    <Box>

      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 3,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(4),
        borderRadius: 2,
        flexDirection: isMobile ? "column" : "row",
        gap: 2,
      }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h4" fontWeight={"bold"} gutterBottom>
              Products Plans Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage subscription plans for your products.
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingPlan(null); setDialogOpen(true); }}>
          Create Plan
        </Button>
      </Box>


      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search plans..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>App Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="App Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
                <MenuItem value="files">Files</MenuItem>
                <MenuItem value="esign">Esign</MenuItem>
                <MenuItem value="projects">Projects</MenuItem>
                <MenuItem value="chat">Chat</MenuItem>
                <MenuItem value="app">App</MenuItem>
                <MenuItem value="campaigns">Campaigns</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Interval</InputLabel>
              <Select
                value={intervalFilter}
                onChange={(e) => setIntervalFilter(e.target.value)}
                label="Interval"
              >
                <MenuItem value="">All Intervals</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="year">Year</MenuItem>
                <MenuItem value="one-time">One-time</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={isEnabledFilter}
                onChange={(e) => setIsEnabledFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" fontWeight="medium">
          {isLoading ? "Searching..." : `Showing ${plansData?.plans?.length || 0} of ${plansData?.totalPlans || 0} plans`}
        </Typography>
      </Box>

      <Card sx={{ backgroundColor: "transparent", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Interval</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>App Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : plansData?.plans?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No plans found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  plansData?.plans?.map((plan) => (
                    <TableRow key={plan._id}>
                      <TableCell>{plan.name}</TableCell>
                      <TableCell>{plan.title}</TableCell>
                      <TableCell>{plan.interval}</TableCell>
                      <TableCell>${plan.amount}</TableCell>
                      <TableCell>{plan.type}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.isEnabled ? "Active" : "Inactive"}
                          size="small"
                          color={plan.isEnabled ? "success" : "default"}
                          variant={plan.isEnabled ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggle(plan)}
                            color={plan.isEnabled ? "success" : "default"}
                            disabled={toggleMutation.isPending}
                          >
                            {plan.isEnabled ? <ViewIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                          <IconButton onClick={() => handleEdit(plan)} color="primary"><EditIcon /></IconButton>
                          <IconButton onClick={() => handleDeleteClick(plan._id)} color="error"><DeleteIcon /></IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <RowPerPage
          rowsPerPage={rowsPerPage}
          onRowsChange={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setPage(1);
          }}
        />
        <Pagination
          count={plansData?.totalPlans || 0}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={(newPage) => setPage(newPage + 1)}
        />
      </Box>

      <PlanForm
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        plan={editingPlan}
        onSubmit={handleSubmit}
        isEditing={!!editingPlan}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this plan? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Plans;
